import Vue from "vue"
import api from "@/api"
import { store } from "@/store/stores"
import ownHealthFeatureAccess from "./ownHealthFeatureAccess"
import trainingFeatureAccess from "./trainingFeatureAccess"
import customerHubPreferences from "./customerHubPreferences"
import healthConnect from "./healthConnectService"
import { healthDay, moveHealthDay } from "./healthStatistics"
import { BODY_READ_TYPES, MESCAN_EXPORT_TYPES, parseMeScans, parseBodyRecords, meScanExportPayload } from "./mescanHealthData"

const loadErrors = {
  MESCAN_PROVIDER_TIMEOUT: "MeScan hat nicht rechtzeitig geantwortet. Bitte versuche es erneut.",
  MESCAN_PROVIDER_UNREACHABLE: "Die well come FIT APP kann den MeScan-Anbieter gerade nicht erreichen. Bitte versuche es erneut.",
  MESCAN_PROVIDER_HTTP_ERROR: "Der MeScan-Anbieter hat den Abruf abgelehnt. Bitte melde dich bei deinem Studio, wenn das erneut passiert.",
  MESCAN_PROVIDER_REJECTED: "Der MeScan-Anbieter meldet einen Fehler beim Abruf deiner Messungen. Bitte melde dich bei deinem Studio.",
  MESCAN_RESPONSE_INVALID: "MeScan hat eine unerwartete Antwort geliefert. Bitte melde dich bei deinem Studio.",
  MESCAN_MEASUREMENT_INVALID: "Eine MeScan-Messung konnte nicht gelesen werden. Bitte melde dich bei deinem Studio.",
  MESCAN_NORMALIZATION_FAILED: "Deine MeScan-Auswertung konnte nicht aufbereitet werden. Bitte melde dich bei deinem Studio.",
}
function loadError(error) {
  const code = error?.response?.data?.code
  if (typeof code === "string" && Object.prototype.hasOwnProperty.call(loadErrors, code)) return loadErrors[code]
  if (error?.response?.status >= 500) return "MeScan konnte serverseitig nicht geladen werden. Bitte versuche es erneut."
  return "MeScan ist gerade nicht erreichbar. Bitte versuche es erneut."
}

// No Vuex persistence, localStorage or upload of imported body/vital measurements.
class MeScanHealthService {
  constructor() {
    this.state = Vue.observable({ scope: "", scans: [], busy: false, loaded: false, error: "", truncated: false,
      records: [], externalBusy: false, externalError: "", externalLimited: false, externalTruncated: false,
      exportBusy: false, exportMessage: "", exportError: "" })
    this.generation = 0; this.externalVersion = 0; this.loadVersion = 0
    this.consumers = 0; this.externalPending = null; this.externalCache = null
  }
  scope() {
    return ownHealthFeatureAccess.isAllowed()
      ? `${ownHealthFeatureAccess.state.userId}:${ownHealthFeatureAccess.state.revision}:${customerHubPreferences.isEnabled() ? "pilot" : "legacy"}:${customerHubPreferences.state.revision}` : ""
  }
  current(scope, generation) { return Boolean(scope && this.scope() === scope && this.state.scope === scope && this.generation === generation) }
  reset() {
    this.generation++; this.externalVersion++; this.loadVersion++
    this.externalPending = null; this.externalCache = null
    Object.assign(this.state, { scope: this.scope(), scans: [], busy: false, loaded: false, error: "", truncated: false,
      records: [], externalBusy: false, externalError: "", externalLimited: false, externalTruncated: false,
      exportBusy: false, exportMessage: "", exportError: "" })
  }
  start() {
    if (this.unwatch) return
    this.reset()
    this.unsubscribe = ownHealthFeatureAccess.subscribe(() => { if (this.scope() !== this.state.scope) { this.reset(); this.load() } })
    this.unwatch = store.watch(() => `${this.scope()}|${healthConnect.state.permissionKey}|${healthConnect.state.enabled}|${healthConnect.state.bodySupported}`, () => {
      if (this.scope() !== this.state.scope) { this.reset(); this.load() }
      else { this.externalVersion++; this.state.records = []; this.state.externalBusy = false; this.state.externalError = "" }
    })
    this.load()
  }
  acquire() { this.consumers++; this.start() }
  release() { this.consumers = Math.max(0, this.consumers - 1); if (!this.consumers) this.stop() }
  stop() { this.unwatch?.(); this.unwatch = null; this.unsubscribe?.(); this.unsubscribe = null; this.reset() }
  async load(force = false) {
    if (this.scope() !== this.state.scope) this.reset()
    const scope = this.scope(), generation = this.generation
    if (!scope || this.state.busy || (this.state.loaded && !force)) return
    const version = ++this.loadVersion, current = () => this.current(scope, generation) && version === this.loadVersion
    const selfOnly = customerHubPreferences.isEnabled(), owner = ownHealthFeatureAccess.state.userId
    this.state.busy = true; this.state.error = ""
    try {
      const response = selfOnly ? await api.fetchOwnHealthMescanData() : await api.fetchHealthMescanData()
      if (!current()) return
      if (selfOnly && (response?.success !== true || String(response.userId || "") !== owner)) throw new Error("Die MeScan-Antwort gehört nicht zur aktuellen Anmeldung.")
      this.state.scans = parseMeScans(response); this.state.truncated = response.truncated === true; this.state.loaded = true
    } catch (error) {
      if (current()) {
        await ownHealthFeatureAccess.reject(ownHealthFeatureAccess.capture(), error)
        if (current()) this.state.error = loadError(error)
      }
    } finally { if (current()) this.state.busy = false }
  }
  loadExternal(force = false) {
    const key = `${this.scope()}|${healthConnect.state.permissionKey}|${healthConnect.state.enabled}|${healthConnect.state.bodySupported}`
    if (this.externalPending?.key === key) return this.externalPending.promise
    if (!force && this.externalCache?.key === key && Date.now() - this.externalCache.at < 30000) return Promise.resolve()
    const request = { key, promise: null }
    this.externalPending = request
    request.promise = this.readExternal().then((loaded) => {
      if (loaded && this.externalPending === request && !this.state.externalError && healthConnect.state.enabled) this.externalCache = { key, at: Date.now() }
    }).finally(() => { if (this.externalPending === request) this.externalPending = null })
    return request.promise
  }
  async readExternal() {
    const scope = this.scope(), generation = this.generation, version = ++this.externalVersion
    const current = () => this.current(scope, generation) && version === this.externalVersion
    this.state.records = []; this.state.externalError = ""; this.state.externalBusy = false
    this.state.externalLimited = false; this.state.externalTruncated = false
    if (!scope || !healthConnect.state.bodySupported || !healthConnect.state.enabled || healthConnect.state.busy) return
    const categories = ["weight", ...BODY_READ_TYPES.map(item => item.key)]
    if (!healthConnect.state.grantedCategories.some(key => categories.includes(key))) return
    this.state.externalBusy = true
    try {
      healthConnect.applyStatus(await healthConnect.native("healthConnectStatus"))
      // A changed permission set invalidates this read through the watcher.
      await Vue.nextTick()
      if (!current() || !healthConnect.state.enabled) return
      const to = healthDay(), from = moveHealthDay(to, -364)
      const permission = healthConnect.state.permissionKey
      const result = await healthConnect.native("healthConnectBody", { from, to }, 45000)
      if (!current() || permission !== healthConnect.state.permissionKey) return
      this.state.records = parseBodyRecords(result)
      this.state.externalLimited = result.limited === true; this.state.externalTruncated = result.truncated === true
      return true
    } catch (error) { if (current()) this.state.externalError = error.message || "Health-Connect-Werte konnten nicht geladen werden." }
    finally { if (current()) this.state.externalBusy = false }
  }
  async exportScans(ids, categories, confirmed) {
    const scope = this.scope(), generation = this.generation, current = () => this.current(scope, generation)
    if (!scope || !trainingFeatureAccess.isAllowed() || !confirmed || this.state.exportBusy || healthConnect.state.busy) return
    this.state.exportError = ""; this.state.exportMessage = ""
    if (!healthConnect.state.bodySupported) { this.state.exportError = "Zum Übertragen brauchst du die neue Android-App."; return }
    const selected = MESCAN_EXPORT_TYPES.map(item => item.exportKey).filter(key => categories.includes(key))
    const scans = meScanExportPayload(this.state.scans.filter(scan => ids.includes(scan.id)), selected)
    if (!selected.length || !scans.length) { this.state.exportError = "Für diese Auswahl sind keine übertragbaren Messwerte vorhanden."; return }
    this.state.exportBusy = true; healthConnect.state.busy = true
    const healthEpoch = healthConnect.epoch
    let written = 0
    const skipped = new Set()
    try {
      healthConnect.applyStatus(await healthConnect.native("healthConnectAuthorizeMescan", { categories: selected }, 300000))
      if (!current()) return
      if (!healthConnect.state.mescanExportGranted.length) throw new Error("Die Schreibfreigabe wurde nicht erteilt. Es wurden keine Werte übertragen.")
      for (let index = 0; index < scans.length; index += 20) {
        if (!current()) return
        const result = await healthConnect.native("healthConnectExportMescan", { scans: scans.slice(index, index + 20), confirmed: true })
        if (!current()) return
        written += Number(result.written) || 0
        ;(result.skippedCategories || []).forEach(key => skipped.add(key))
        this.state.exportMessage = `${written} Messwerte übertragen.`
      }
      const missing = MESCAN_EXPORT_TYPES.filter(item => skipped.has(item.exportKey)).map(item => item.label)
      if (missing.length) this.state.exportMessage += ` Nicht freigegeben: ${missing.join(", ")}.`
    } catch (error) { if (current()) this.state.exportError = `${written ? `${written} Messwerte bereits übertragen. ` : ""}${error.message || "Die Übertragung ist fehlgeschlagen."} Erneutes Übertragen erzeugt keine zusätzlichen Einträge.` }
    finally {
      if (current()) this.state.exportBusy = false
      if (healthEpoch === healthConnect.epoch) healthConnect.state.busy = false
    }
  }

  async trainingContext(from, to) {
    const scope = this.scope(), generation = this.generation
    if (!scope) return null
    try {
      const result = await api.fetchMescanTrainingContext(from, to)
      return this.current(scope, generation) && result?.from === from && result?.to === to ? result : null
    } catch (error) {
      if (this.current(scope, generation)) await ownHealthFeatureAccess.reject(ownHealthFeatureAccess.capture(), error)
      throw new Error("Deine Trainings konnten nicht geladen werden.")
    }
  }
}
export default new MeScanHealthService()
